import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import i18n from '../../i18n';
import ConfigurationProperty from './ConfigurationProperty';
import { ConfigType, ConfigurationMap, ConfProp } from '../../types/rest/configurationsType';
import Button from 'react-bootstrap/Button';

interface ConfigurationPropertyListProps {
  id: string;
  configurationProperties: ConfProp[] | null;
  handleAddNew: (type: ConfigType) => void;
  onListChange: (list: ConfProp[]) => void;
  isReadOnly?: boolean;
  onSave: (configurationMap: ConfigurationMap, duplicatesFound: boolean) => void;
  invalidField: boolean;
  inInitialState: boolean;
  dirtyPropsFound: boolean;
  resetDirtyPropsFound: () => void;
}

export interface ColSizes {
  md: ('auto' | number)[];
  lg: ('auto' | number)[];
}

const colSizes: ColSizes = {
  md: [4, 4, 4],
  lg: [4, 4, 3],
};

const ConfigurationPropertyList = (props: ConfigurationPropertyListProps) => {
  const { t } = useTranslation('transactions');

  const handleDelete = (index: number) => {
    if (props.configurationProperties) {
      const updated = _.cloneDeep(props.configurationProperties);
      updated.splice(index, 1);
      props.onListChange(updated);
    }
  };

  const handleChange = (confProp: ConfProp, index: number) => {
    if (_.isNil(props.configurationProperties)) {
      return;
    }

    const list: ConfProp[] = _.cloneDeep(props.configurationProperties);
    if (index === -1) {
      console.log(`Cannot update value! Cannot find ${index} in the list.`);
    } else {
      let isValueChanged: boolean;
      if (list[index].configuration.type === 'string') {
        isValueChanged = list[index].configuration.value !== confProp.configuration.value;
      } else {
        isValueChanged =
          list[index].configuration.assetId !== confProp.configuration.assetId ||
          list[index].configuration.alias !== confProp.configuration.alias;
      }

      const isKeyOrValueChanged = isValueChanged || list[index].key !== confProp.key;
      if (isKeyOrValueChanged) {
        confProp.isDirty = true;
      }
      list[index] = confProp;
      props.onListChange(list);
    }
  };

  const saveConfigurations = (duplicatesFound: boolean) => {
    //Create config map for backend
    const cm = {} as ConfigurationMap;
    _.each(props.configurationProperties, confProp => {
      cm[confProp.key] = {
        type: confProp.configuration.type,
        value: confProp.configuration.value,
        alias: confProp.configuration.alias,
        assetId: confProp.configuration.assetId,
      };
    });
    props.onSave(cm, duplicatesFound);
  };

  const getComponentKey = (index: number) => {
    return 'confProperty-' + index;
  };

  const isSaveDisabled = (validationFound: boolean, emptyFieldsFound: boolean, emptyCertFound: boolean) => {
    //new configuration being created
    if (props.id === 'list--1' && emptyCertFound !== true) {
      return false;
    }
    // validate empty name of configuration
    // validate empty certificates
    // validate empty fields
    if (props.invalidField === true || emptyCertFound === true || emptyFieldsFound === true) {
      return true;
    }

    // any other validation: max length, invalid character, duplicates
    return validationFound;
  };

  // new rows are not validated, so we need to verify the list
  const emptyFieldsFound = props.configurationProperties.some(cp => cp.key.trim() === '');

  // //only when is keystore validate if alias is present, other extension ignore
  const blankCertFound = props.configurationProperties.some(
    cp => cp.configuration.type === 'asset' && _.isNil(cp?.configuration?.assetId),
  );

  const invalidElementFound = props.configurationProperties.some(c => c.isDirty === true && c.isValid === false);

  return (
    <div id={'qa-' + props.id}>
      <Row className={'mb-2'}>
        <Col md={8}>
          <h4>{i18n.t('configurations:properties.title')}</h4>
        </Col>
        <Col md={4} className="text-md-right">
          <Button
            disabled={isSaveDisabled(invalidElementFound, emptyFieldsFound, blankCertFound)}
            onClick={() => saveConfigurations(invalidElementFound || emptyFieldsFound)}
            id="qa-btn-save-transaction"
            title={i18n.t('transactions:editor.saveTxn')}
          >
            {i18n.t('common:actions.save')}
          </Button>
        </Col>
      </Row>
      {/* Header */}
      <Row>
        <Col md={colSizes.md[0]} lg={colSizes.lg[0]}>
          <Form.Label>{t('configurations:properties.name')}</Form.Label>
        </Col>
        <Col md={colSizes.md[1]} lg={colSizes.lg[1]}>
          <Form.Label>{t('configurations:properties.value')}</Form.Label>
        </Col>
        <Col md={colSizes.md[2]} lg={colSizes.lg[2]}></Col>
      </Row>
      {/* ./ Header */}

      {props.configurationProperties.map((configProperty: ConfProp, index: number) => {
        const propKeysList = props.configurationProperties
          .filter((cp, subIndex) => subIndex !== index && cp.key.trim() !== '')
          .map(cp2 => cp2.key);
        if (props.dirtyPropsFound === true) {
          configProperty.isDirty = true;
        }
        return (
          <ConfigurationProperty
            onChange={(confProp: ConfProp) => handleChange(confProp, index)}
            onAdd={props.handleAddNew}
            onDelete={() => handleDelete(index)}
            key={getComponentKey(index)}
            id={getComponentKey(index)}
            colSizes={colSizes}
            isReadOnly={props.isReadOnly}
            showAddIcon={index === _.size(props.configurationProperties) - 1}
            showDeleteIcon={_.size(props.configurationProperties) !== 1}
            configurationProperty={configProperty}
            currentConfigurationKeys={propKeysList}
            inInitialState={props.inInitialState}
          />
        );
      })}
      {props.dirtyPropsFound === true && props.resetDirtyPropsFound()}
    </div>
  );
};

export default ConfigurationPropertyList;
